// src/modules/hr/pages/AssetManagement.jsx — Phase 15
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Input, Select, Tag, Tooltip, Modal,
  Form, DatePicker, InputNumber, message, Space,
} from 'antd';
import { LaptopOutlined, ReloadOutlined, PlusOutlined, SwapOutlined, RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchBranches } from '../../branches/api';
import { fetchEmployees, fetchAssets, createAsset, assignAsset, returnAsset } from '../api';
import {
  ASSET_CATEGORY_OPTIONS, ASSET_STATUS_OPTIONS, ASSET_CONDITION_OPTIONS, tagColor, tagLabel,
} from '../constants/hrConstants';

const { Title, Text } = Typography;

export default function AssetManagement() {
  const { can } = usePermissions();
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [filters, setFilters] = useState({ search: '', category: null, status: null });
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [addForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [returnForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBranches({ limit: 1000 }).then((r) => setBranches(r?.data?.data || r?.data || [])).catch(() => {});
    fetchEmployees({ limit: 1000 }).then((r) => setEmployees(r?.data?.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.pageSize };
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      const res = await fetchAssets(params);
      setData(res?.data?.data || []);
      setPagination((p) => ({ ...p, current: res?.data?.pagination?.current || 1, total: res?.data?.pagination?.total || 0 }));
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load assets'); }
    finally { setLoading(false); }
  }, [filters, pagination.pageSize]);

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [filters]);

  const submitAdd = async () => {
    try {
      const v = await addForm.validateFields();
      setSaving(true);
      await createAsset({
        ...v,
        purchaseDate: v.purchaseDate?.toISOString(), warrantyStart: v.warrantyStart?.toISOString(), warrantyEnd: v.warrantyEnd?.toISOString(),
      });
      message.success('Asset added'); setAddOpen(false); addForm.resetFields(); load(1);
    } catch (err) { if (!err?.errorFields) message.error(err?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const submitAssign = async () => {
    try {
      const v = await assignForm.validateFields();
      setSaving(true);
      await assignAsset(assignTarget._id, { ...v, issueDate: v.issueDate?.toISOString() });
      message.success('Asset assigned'); setAssignTarget(null); assignForm.resetFields(); load(pagination.current);
    } catch (err) { if (!err?.errorFields) message.error(err?.response?.data?.message || 'Assign failed'); }
    finally { setSaving(false); }
  };

  const submitReturn = async () => {
    try {
      const v = await returnForm.validateFields();
      setSaving(true);
      await returnAsset(returnTarget._id, { ...v, returnedDate: v.returnedDate?.toISOString() });
      message.success('Asset returned'); setReturnTarget(null); returnForm.resetFields(); load(pagination.current);
    } catch (err) { if (!err?.errorFields) message.error(err?.response?.data?.message || 'Return failed'); }
    finally { setSaving(false); }
  };

  const columns = [
    { title: 'Code', dataIndex: 'assetCode', width: 110, render: (v) => <Text code>{v}</Text> },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Category', dataIndex: 'category', width: 150, render: (v) => <Tag>{v?.replace(/_/g, ' ')}</Tag> },
    { title: 'Serial', dataIndex: 'serialNumber', render: (v) => v || '—' },
    { title: 'Branch', dataIndex: ['branch', 'name'], render: (v, r) => r.branch?.name || '—' },
    {
      title: 'Holder', key: 'holder',
      render: (_, r) => {
        if (r.assignedToEmployee && r.assignedToBranch) return `${r.assignedToEmployee.name} @ ${r.assignedToBranch.name}`;
        return r.assignedToEmployee?.name || r.assignedToBranch?.name || <Text type="secondary">—</Text>;
      },
    },
    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag color={tagColor(ASSET_STATUS_OPTIONS, v)}>{tagLabel(ASSET_STATUS_OPTIONS, v)}</Tag> },
    {
      title: 'Actions', key: 'a', width: 130, fixed: 'right',
      render: (_, r) => (
        <Space>
          {can('hrAssets', 'edit') && r.status === 'AVAILABLE' && <Tooltip title="Assign"><Button size="small" icon={<SwapOutlined />} onClick={() => { setAssignTarget(r); assignForm.setFieldsValue({ assigneeType: 'EMPLOYEE', issueDate: dayjs() }); }} /></Tooltip>}
          {can('hrAssets', 'edit') && r.status === 'ASSIGNED' && <Tooltip title="Return"><Button size="small" icon={<RollbackOutlined />} onClick={() => { setReturnTarget(r); returnForm.setFieldsValue({ returnedDate: dayjs(), returnCondition: 'GOOD' }); }} /></Tooltip>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><LaptopOutlined style={{ marginRight: 8 }} />Asset Management</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Company asset register, assignment & return</Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={() => load(pagination.current)} /></Tooltip>
            {can('hrAssets', 'create') && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add Asset</Button>}
          </Space>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]}>
          <Col xs={24} sm={10}><Input allowClear prefix={<SearchOutlined />} placeholder="Search name / code / serial" onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} /></Col>
          <Col xs={12} sm={7}><Select allowClear style={{ width: '100%' }} placeholder="Category" options={ASSET_CATEGORY_OPTIONS} value={filters.category} onChange={(v) => setFilters((f) => ({ ...f, category: v || null }))} /></Col>
          <Col xs={12} sm={7}><Select allowClear style={{ width: '100%' }} placeholder="Status" options={ASSET_STATUS_OPTIONS} value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v || null }))} /></Col>
        </Row>
      </Card>

      <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" scroll={{ x: 1100 }}
        pagination={{ ...pagination, showSizeChanger: true, showTotal: (t) => `${t} assets`, onChange: (p) => load(p) }} />

      {/* Add */}
      <Modal title="Add Asset" open={addOpen} onCancel={() => setAddOpen(false)} onOk={submitAdd} okButtonProps={{ loading: saving }} width={640} destroyOnClose>
        <Form form={addForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}><Form.Item name="name" label="Asset Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="category" label="Category" rules={[{ required: true }]}><Select options={ASSET_CATEGORY_OPTIONS} showSearch optionFilterProp="label" /></Form.Item></Col>
            <Col span={12}><Form.Item name="vendor" label="Vendor"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="serialNumber" label="Serial Number"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="purchaseDate" label="Purchase Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="purchaseCost" label="Purchase Cost"><InputNumber style={{ width: '100%' }} min={0} prefix="₹" /></Form.Item></Col>
            <Col span={8}><Form.Item name="invoiceNumber" label="Invoice No."><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="warrantyStart" label="Warranty Start"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="warrantyEnd" label="Warranty End"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="branch" label="Branch"><Select showSearch optionFilterProp="children" allowClear>{branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}</Select></Form.Item></Col>
            <Col span={24}><Form.Item name="invoiceCopy" label="Invoice Copy URL"><Input placeholder="Paste uploaded file URL" /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* Assign */}
      <Modal title={`Assign — ${assignTarget?.name || ''}`} open={!!assignTarget} onCancel={() => setAssignTarget(null)} onOk={submitAssign} okButtonProps={{ loading: saving }} destroyOnClose>
        <Form form={assignForm} layout="vertical">
          <Form.Item name="assigneeType" label="Assign To" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'Employee only', value: 'EMPLOYEE' },
                { label: 'Branch only', value: 'BRANCH' },
                { label: 'Branch + Employee', value: 'BRANCH_EMPLOYEE' },
              ]}
              onChange={() => { assignForm.setFieldsValue({ employeeId: undefined, branchId: undefined }); }}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.assigneeType !== c.assigneeType}>
            {({ getFieldValue }) => {
              const t = getFieldValue('assigneeType');
              return (
                <>
                  {(t === 'BRANCH' || t === 'BRANCH_EMPLOYEE') && (
                    <Form.Item name="branchId" label="Branch" rules={[{ required: true }]}>
                      <Select showSearch optionFilterProp="children">
                        {branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}
                      </Select>
                    </Form.Item>
                  )}
                  {(t === 'EMPLOYEE' || t === 'BRANCH_EMPLOYEE') && (
                    <Form.Item name="employeeId" label="Employee" rules={[{ required: true }]}>
                      <Select showSearch optionFilterProp="children">
                        {employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name} {e.employeeCode ? `(${e.employeeCode})` : ''}</Select.Option>)}
                      </Select>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="issueDate" label="Issue Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="issueCondition" label="Condition"><Select options={ASSET_CONDITION_OPTIONS} /></Form.Item></Col>
          </Row>
          <Form.Item name="issueRemarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Return */}
      <Modal title={`Return — ${returnTarget?.name || ''}`} open={!!returnTarget} onCancel={() => setReturnTarget(null)} onOk={submitReturn} okButtonProps={{ loading: saving }} destroyOnClose>
        <Form form={returnForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}><Form.Item name="returnedDate" label="Returned Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="returnCondition" label="Condition"><Select options={ASSET_CONDITION_OPTIONS} /></Form.Item></Col>
            <Col span={12}><Form.Item name="penalty" label="Penalty"><InputNumber style={{ width: '100%' }} min={0} prefix="₹" /></Form.Item></Col>
            <Col span={24}><Form.Item name="damage" label="Damage"><Input /></Form.Item></Col>
            <Col span={24}><Form.Item name="returnRemarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
